import { APIFieldType, Operator, ReferenceFilterIds } from '@ardoq/api-types';
import { TAGS_FIELD_NAME } from '@ardoq/data-model';
import { DEFAULT_ARDOQ_I18NLOCALE, type I18nLocale, t } from '@ardoq/i18n';
import { SearchContext } from '../types';
import { createReferenceTypeFilter } from './createGenericFilter';
import {
  DynamicFilterValues,
  QueryBuilderFilterDefinition,
  QueryBuilderFilterType,
} from './types';

// DEFAULT FILTERS — factories
export const getArdoqIdFilter = (i18nLocale: I18nLocale) => ({
  id: 'component-key',
  label: t(i18nLocale, 'queryBuilder:filterAttribute.ardoqId', 'Ardoq ID'),
  filterType: QueryBuilderFilterType.STRING,
  operators: [Operator.EQUAL, Operator.NOT_EQUAL],
});

export const getArdoqOidFilter = (i18nLocale: I18nLocale) => ({
  id: '_id',
  label: t(i18nLocale, 'queryBuilder:filterAttribute.id', 'Id'),
  filterType: QueryBuilderFilterType.STRING,
  operators: [Operator.EQUAL, Operator.NOT_EQUAL],
});

export const getRevisionFilter = (i18nLocale: I18nLocale) => ({
  id: '_version',
  label: t(i18nLocale, 'queryBuilder:filterAttribute.revision', 'Revision'),
  filterType: QueryBuilderFilterType.NUMBER,
  operators: [
    Operator.EQUAL,
    Operator.NOT_EQUAL,
    Operator.GREATER,
    Operator.LESS,
  ],
});

export const getComponentNameFilter = (i18nLocale: I18nLocale) => ({
  id: 'name',
  label: t(i18nLocale, 'queryBuilder:filterAttribute.name', 'Name'),
  filterType: QueryBuilderFilterType.ASYNC_COMPONENT_NAME,
  operators: [
    Operator.EQUAL,
    Operator.NOT_EQUAL,
    Operator.CONTAINS_SUBSTRING,
    Operator.NOT_CONTAINS_SUBSTRING,
  ],
});

const getWorkspaceNameFilter = (i18nLocale: I18nLocale) => ({
  id: 'name',
  label: t(i18nLocale, 'queryBuilder:filterAttribute.name', 'Name'),
  filterType: QueryBuilderFilterType.ASYNC_WORKSPACE_NAME,
  operators: [
    Operator.EQUAL,
    Operator.NOT_EQUAL,
    Operator.CONTAINS_SUBSTRING,
    Operator.NOT_CONTAINS_SUBSTRING,
  ],
});

export const getDescriptionFilter = (i18nLocale: I18nLocale) => ({
  id: 'description',
  label: t(
    i18nLocale,
    'queryBuilder:filterAttribute.description',
    'Description'
  ),
  filterType: QueryBuilderFilterType.STRING,
  operators: [
    Operator.CONTAINS_SUBSTRING,
    Operator.NOT_CONTAINS_SUBSTRING,
    Operator.IS_EMPTY,
    Operator.IS_NOT_EMPTY,
  ],
});

export const getDisplayTextFilter = (i18nLocale: I18nLocale) => ({
  id: 'displayText',
  label: t(
    i18nLocale,
    'queryBuilder:filterAttribute.displayText',
    'Display text'
  ),
  filterType: QueryBuilderFilterType.STRING,
  operators: [
    Operator.CONTAINS_SUBSTRING,
    Operator.NOT_CONTAINS_SUBSTRING,
    Operator.IS_EMPTY,
    Operator.IS_NOT_EMPTY,
  ],
});

export const getCreatedFilter = (i18nLocale: I18nLocale) => ({
  id: 'created',
  label: t(i18nLocale, 'queryBuilder:filterAttribute.created', 'Created'),
  filterType: QueryBuilderFilterType.DATE_TIME,
  operators: [
    Operator.EQUAL,
    Operator.GREATER,
    Operator.LESS,
    Operator.BETWEEN,
  ],
});

export const getCreatedByFilter = (i18nLocale: I18nLocale) => ({
  id: 'created-by',
  label: t(i18nLocale, 'queryBuilder:filterAttribute.createdBy', 'Created by'),
  filterType: QueryBuilderFilterType.ASYNC_USER,
  operators: [
    Operator.EQUAL,
    Operator.NOT_EQUAL,
    Operator.CONTAINS_SUBSTRING,
    Operator.NOT_CONTAINS_SUBSTRING,
    Operator.IS_EMPTY,
    Operator.IS_NOT_EMPTY,
  ],
});

export const getLastUpdatedFilter = (i18nLocale: I18nLocale) => ({
  id: 'last-updated',
  label: t(
    i18nLocale,
    'queryBuilder:filterAttribute.lastUpdated',
    'Last updated'
  ),
  filterType: QueryBuilderFilterType.DATE_TIME,
  operators: [
    Operator.EQUAL,
    Operator.GREATER,
    Operator.LESS,
    Operator.BETWEEN,
  ],
});

export const getLastModifiedByFilter = (i18nLocale: I18nLocale) => ({
  id: 'last-modified-by',
  label: t(i18nLocale, 'queryBuilder:filterAttribute.updatedBy', 'Updated by'),
  filterType: QueryBuilderFilterType.ASYNC_USER,
  operators: [
    Operator.EQUAL,
    Operator.NOT_EQUAL,
    Operator.CONTAINS_SUBSTRING,
    Operator.NOT_CONTAINS_SUBSTRING,
    Operator.IS_EMPTY,
    Operator.IS_NOT_EMPTY,
  ],
});

export const getTagsFilter = (i18nLocale: I18nLocale) => ({
  id: TAGS_FIELD_NAME,
  label: t(i18nLocale, 'queryBuilder:filterAttribute.tags', 'Tags'),
  filterType: QueryBuilderFilterType.ASYNC_TAGS,
  operators: [
    Operator.EQUAL,
    Operator.NOT_EQUAL,
    Operator.IS_EMPTY,
    Operator.IS_NOT_EMPTY,
  ],
});

export const getRootWorkspaceFilter = (i18nLocale: I18nLocale) => ({
  id: 'rootWorkspace',
  label: t(i18nLocale, 'queryBuilder:filterAttribute.workspace', 'Workspace'),
  filterType: QueryBuilderFilterType.ASYNC_ROOT_WORKSPACE,
  operators: [Operator.EQUAL, Operator.NOT_EQUAL],
});

export const getAsyncComponentRootWorkspaceFilter = (
  i18nLocale: I18nLocale
): QueryBuilderFilterDefinition => ({
  id: 'rootWorkspace',
  label: t(
    i18nLocale,
    'queryBuilder:filterAttribute.belongsToWorkspace',
    'Belongs to workspace'
  ),
  filterType: QueryBuilderFilterType.ASYNC_ROOT_WORKSPACE,
  operators: [Operator.EQUAL, Operator.NOT_EQUAL],
});

export const getSourceFilter = (i18nLocale: I18nLocale) => ({
  id: 'source',
  label: t(
    i18nLocale,
    'queryBuilder:filterAttribute.referenceFrom',
    'Reference from'
  ),
  filterType: QueryBuilderFilterType.ASYNC_SOURCE,
  operators: [Operator.EQUAL, Operator.NOT_EQUAL],
});

export const getTargetFilter = (i18nLocale: I18nLocale) => ({
  id: 'target',
  label: t(
    i18nLocale,
    'queryBuilder:filterAttribute.referenceTo',
    'Reference to'
  ),
  filterType: QueryBuilderFilterType.ASYNC_TARGET,
  operators: [Operator.EQUAL, Operator.NOT_EQUAL],
});

export const getParentFilter = (i18nLocale: I18nLocale) => ({
  id: 'parent',
  label: t(
    i18nLocale,
    'queryBuilder:filterAttribute.parentComponent',
    'Parent component'
  ),
  filterType: QueryBuilderFilterType.ASYNC_PARENT,
  operators: [
    Operator.EQUAL,
    Operator.NOT_EQUAL,
    Operator.IS_EMPTY,
    Operator.IS_NOT_EMPTY,
  ],
});

export const getChildCountFilter = (i18nLocale: I18nLocale) => ({
  id: 'child-count',
  label: t(
    i18nLocale,
    'queryBuilder:filterAttribute.childCount',
    'Child count'
  ),
  filterType: QueryBuilderFilterType.NUMBER,
  operators: [
    Operator.EQUAL,
    Operator.NOT_EQUAL,
    Operator.GREATER,
    Operator.LESS,
  ],
});

export const getComponentTypeNameFilter = (i18nLocale: I18nLocale) => ({
  id: 'typeName',
  label: t(i18nLocale, 'queryBuilder:filterAttribute.type', 'Type'),
  filterType: QueryBuilderFilterType.ASYNC_COMPONENT_TYPE_NAME,
  operators: [
    Operator.EQUAL,
    Operator.NOT_EQUAL,
    Operator.CONTAINS_SUBSTRING,
    Operator.NOT_CONTAINS_SUBSTRING,
  ],
});

export const getReferenceTypeNameFilter = (i18nLocale: I18nLocale) => ({
  id: 'typeName',
  label: t(i18nLocale, 'queryBuilder:filterAttribute.type', 'Type'),
  filterType: QueryBuilderFilterType.ASYNC_REFERENCE_TYPE_NAME,
  operators: [
    Operator.EQUAL,
    Operator.NOT_EQUAL,
    Operator.CONTAINS_SUBSTRING,
    Operator.NOT_CONTAINS_SUBSTRING,
  ],
});

export const getReferenceTargetTypeNameFilter = (i18nLocale: I18nLocale) => ({
  id: 'ardoq.targetComponentTypeName',
  label: t(
    i18nLocale,
    'queryBuilder:filterAttribute.targetComponentType',
    'Target Component Type'
  ),
  filterType: QueryBuilderFilterType.ASYNC_COMPONENT_TYPE_NAME,
  operators: [
    Operator.EQUAL,
    Operator.NOT_EQUAL,
    Operator.CONTAINS_SUBSTRING,
    Operator.NOT_CONTAINS_SUBSTRING,
  ],
});

export const getReferenceSourceTypeNameFilter = (i18nLocale: I18nLocale) => ({
  id: 'ardoq.sourceComponentTypeName',
  label: t(
    i18nLocale,
    'queryBuilder:filterAttribute.sourceComponentType',
    'Source Component Type'
  ),
  filterType: QueryBuilderFilterType.ASYNC_COMPONENT_TYPE_NAME,
  operators: [
    Operator.EQUAL,
    Operator.NOT_EQUAL,
    Operator.CONTAINS_SUBSTRING,
    Operator.NOT_CONTAINS_SUBSTRING,
  ],
});

export enum ReferenceTypeFilterIds {
  HAS_OUTGOING_REFERENCE_TYPE = 'has-outgoing-reference-type',
  DOES_NOT_HAVE_OUTGOING_REFERENCE_TYPE = 'does-not-have-outgoing-reference-type',
  HAS_INCOMING_REFERENCE_TYPE = 'has-incoming-reference-type',
  DOES_NOT_HAVE_INCOMING_REFERENCE_TYPE = 'does-not-have-incoming-reference-type',
}

const getHasOutgoingReferenceTypeFilter = (i18nLocale: I18nLocale) =>
  createReferenceTypeFilter(
    ReferenceTypeFilterIds.HAS_OUTGOING_REFERENCE_TYPE,
    t(
      i18nLocale,
      'queryBuilder:filterAttribute.hasOutgoingReferenceType',
      'Has outgoing reference type'
    )
  );

const getDoesNotHaveOutgoingReferenceTypeFilter = (i18nLocale: I18nLocale) =>
  createReferenceTypeFilter(
    ReferenceTypeFilterIds.DOES_NOT_HAVE_OUTGOING_REFERENCE_TYPE,
    t(
      i18nLocale,
      'queryBuilder:filterAttribute.doesNotHaveOutgoingReferenceType',
      'Does not have outgoing reference type'
    )
  );

const getHasIncomingReferenceTypeFilter = (i18nLocale: I18nLocale) =>
  createReferenceTypeFilter(
    ReferenceTypeFilterIds.HAS_INCOMING_REFERENCE_TYPE,
    t(
      i18nLocale,
      'queryBuilder:filterAttribute.hasIncomingReferenceType',
      'Has incoming reference type'
    )
  );

const getDoesNotHaveIncomingReferenceTypeFilter = (i18nLocale: I18nLocale) =>
  createReferenceTypeFilter(
    ReferenceTypeFilterIds.DOES_NOT_HAVE_INCOMING_REFERENCE_TYPE,
    t(
      i18nLocale,
      'queryBuilder:filterAttribute.doesNotHaveIncomingReferenceType',
      'Does not have incoming reference type'
    )
  );

const getHasReferenceFilter = (i18nLocale: I18nLocale) => ({
  id: ReferenceFilterIds.HAS_REFERENCE,
  label: t(
    i18nLocale,
    'queryBuilder:filterAttribute.hasReference',
    'Has reference'
  ),
  filterType: QueryBuilderFilterType.ASYNC_SOURCE,
  operators: [Operator.TO, Operator.FROM],
});

const getHasNoReferenceFilter = (i18nLocale: I18nLocale) => ({
  id: ReferenceFilterIds.HAS_NO_REFERENCE,
  label: t(
    i18nLocale,
    'queryBuilder:filterAttribute.hasNoReference',
    'Has no reference'
  ),
  filterType: QueryBuilderFilterType.ASYNC_SOURCE,
  operators: [Operator.TO, Operator.FROM],
});

// Backward-compat: English-frozen constants for callers without locale.
/** @deprecated Use {@link getArdoqIdFilter} with an `I18nLocale`. */
export const ARDOQ_ID = getArdoqIdFilter(DEFAULT_ARDOQ_I18NLOCALE);
/** @deprecated Use {@link getArdoqOidFilter} with an `I18nLocale`. */
export const ARDOQ_OID = getArdoqOidFilter(DEFAULT_ARDOQ_I18NLOCALE);
/** @deprecated Use {@link getRevisionFilter} with an `I18nLocale`. */
export const REVISION = getRevisionFilter(DEFAULT_ARDOQ_I18NLOCALE);
/** @deprecated Use {@link getComponentNameFilter} with an `I18nLocale`. */
export const COMPONENT_NAME = getComponentNameFilter(DEFAULT_ARDOQ_I18NLOCALE);
/** @deprecated Use {@link getDescriptionFilter} with an `I18nLocale`. */
export const DESCRIPTION = getDescriptionFilter(DEFAULT_ARDOQ_I18NLOCALE);
/** @deprecated Use {@link getDisplayTextFilter} with an `I18nLocale`. */
export const DISPLAY_TEXT = getDisplayTextFilter(DEFAULT_ARDOQ_I18NLOCALE);
/** @deprecated Use {@link getCreatedFilter} with an `I18nLocale`. */
export const CREATED = getCreatedFilter(DEFAULT_ARDOQ_I18NLOCALE);
/** @deprecated Use {@link getCreatedByFilter} with an `I18nLocale`. */
export const CREATED_BY = getCreatedByFilter(DEFAULT_ARDOQ_I18NLOCALE);
/** @deprecated Use {@link getLastUpdatedFilter} with an `I18nLocale`. */
export const LAST_UPDATED = getLastUpdatedFilter(DEFAULT_ARDOQ_I18NLOCALE);
/** @deprecated Use {@link getLastModifiedByFilter} with an `I18nLocale`. */
export const LAST_MODIFIED_BY = getLastModifiedByFilter(
  DEFAULT_ARDOQ_I18NLOCALE
);
/** @deprecated Use {@link getTagsFilter} with an `I18nLocale`. */
export const TAGS = getTagsFilter(DEFAULT_ARDOQ_I18NLOCALE);
/** @deprecated Use {@link getRootWorkspaceFilter} with an `I18nLocale`. */
export const ROOT_WORKSPACE = getRootWorkspaceFilter(DEFAULT_ARDOQ_I18NLOCALE);
/** @deprecated Use {@link getAsyncComponentRootWorkspaceFilter} with an `I18nLocale`. */
export const ASYNC_COMPONENT_ROOT_WORKSPACE: QueryBuilderFilterDefinition =
  getAsyncComponentRootWorkspaceFilter(DEFAULT_ARDOQ_I18NLOCALE);
/** @deprecated Use {@link getSourceFilter} with an `I18nLocale`. */
export const SOURCE = getSourceFilter(DEFAULT_ARDOQ_I18NLOCALE);
/** @deprecated Use {@link getTargetFilter} with an `I18nLocale`. */
export const TARGET = getTargetFilter(DEFAULT_ARDOQ_I18NLOCALE);
/** @deprecated Use {@link getParentFilter} with an `I18nLocale`. */
export const PARENT = getParentFilter(DEFAULT_ARDOQ_I18NLOCALE);
/** @deprecated Use {@link getChildCountFilter} with an `I18nLocale`. */
export const CHILD_COUNT = getChildCountFilter(DEFAULT_ARDOQ_I18NLOCALE);
/** @deprecated Use {@link getComponentTypeNameFilter} with an `I18nLocale`. */
export const COMPONENT_TYPE_NAME = getComponentTypeNameFilter(
  DEFAULT_ARDOQ_I18NLOCALE
);
/** @deprecated Use {@link getReferenceTypeNameFilter} with an `I18nLocale`. */
export const REFERENCE_TYPE_NAME = getReferenceTypeNameFilter(
  DEFAULT_ARDOQ_I18NLOCALE
);
/** @deprecated Use {@link getReferenceTargetTypeNameFilter} with an `I18nLocale`. */
export const REFERENCE_TARGET_TYPE_NAME = getReferenceTargetTypeNameFilter(
  DEFAULT_ARDOQ_I18NLOCALE
);
/** @deprecated Use {@link getReferenceSourceTypeNameFilter} with an `I18nLocale`. */
export const REFERENCE_SOURCE_TYPE_NAME = getReferenceSourceTypeNameFilter(
  DEFAULT_ARDOQ_I18NLOCALE
);

export const filterTypeByApiFieldType = (
  /** true if the caller is for conditional formatting, where multiselect is supported for {@link APIFieldType.SELECT_MULTIPLE_LIST} rules. false if the caller is for filter rules, which only support a single value for {@link APIFieldType.SELECT_MULTIPLE_LIST} rules. */
  isConditionalFormatting: boolean
): Record<APIFieldType, QueryBuilderFilterType> => ({
  [APIFieldType.CHECKBOX]: QueryBuilderFilterType.BOOLEAN,
  [APIFieldType.DATE_ONLY_RANGE]: QueryBuilderFilterType.DATE,
  [APIFieldType.DATE_TIME_RANGE]: QueryBuilderFilterType.DATE,
  [APIFieldType.DATE_ONLY]: QueryBuilderFilterType.DATE,
  [APIFieldType.DATE_TIME]: QueryBuilderFilterType.DATE_TIME,
  [APIFieldType.EMAIL]: QueryBuilderFilterType.STRING,
  [APIFieldType.LIST]: QueryBuilderFilterType.SELECT,
  // TODO FIGURE OUT WHAT'S GOING ON WITH THE DOUBLE vs INTEGER
  [APIFieldType.NUMBER]: QueryBuilderFilterType.NUMBER,
  [APIFieldType.SELECT_MULTIPLE_LIST]: isConditionalFormatting
    ? QueryBuilderFilterType.SELECT_MULTIPLE
    : QueryBuilderFilterType.SELECT,
  [APIFieldType.TEXT]: QueryBuilderFilterType.STRING,
  [APIFieldType.TEXT_AREA]: QueryBuilderFilterType.STRING,
  [APIFieldType.URL]: QueryBuilderFilterType.STRING,
  [APIFieldType.USER]: QueryBuilderFilterType.ASYNC_USER,
  [APIFieldType.FILE]: QueryBuilderFilterType.STRING,
});

export const getDefaultFiltersBySearchContext = (
  i18nLocale: I18nLocale
): Record<SearchContext, QueryBuilderFilterDefinition[]> => ({
  [SearchContext.WORKSPACE]: [
    getArdoqOidFilter(i18nLocale),
    getWorkspaceNameFilter(i18nLocale),
    getDescriptionFilter(i18nLocale),
    getCreatedFilter(i18nLocale),
    getCreatedByFilter(i18nLocale),
    getLastUpdatedFilter(i18nLocale),
    getLastModifiedByFilter(i18nLocale),
  ],
  [SearchContext.COMPONENT]: [
    getArdoqOidFilter(i18nLocale),
    getArdoqIdFilter(i18nLocale),
    getComponentNameFilter(i18nLocale),
    getDescriptionFilter(i18nLocale),
    getCreatedFilter(i18nLocale),
    getCreatedByFilter(i18nLocale),
    getLastUpdatedFilter(i18nLocale),
    getLastModifiedByFilter(i18nLocale),
    getRootWorkspaceFilter(i18nLocale),
    getTagsFilter(i18nLocale),
    getComponentTypeNameFilter(i18nLocale),
    getParentFilter(i18nLocale),
    getChildCountFilter(i18nLocale),
    getHasReferenceFilter(i18nLocale),
    getHasNoReferenceFilter(i18nLocale),
    getHasOutgoingReferenceTypeFilter(i18nLocale),
    getDoesNotHaveOutgoingReferenceTypeFilter(i18nLocale),
    getHasIncomingReferenceTypeFilter(i18nLocale),
    getDoesNotHaveIncomingReferenceTypeFilter(i18nLocale),
  ],
  [SearchContext.REFERENCE]: [
    getArdoqOidFilter(i18nLocale),
    getDescriptionFilter(i18nLocale),
    getDisplayTextFilter(i18nLocale),
    getCreatedFilter(i18nLocale),
    getCreatedByFilter(i18nLocale),
    getLastUpdatedFilter(i18nLocale),
    getLastModifiedByFilter(i18nLocale),
    getRootWorkspaceFilter(i18nLocale),
    getTagsFilter(i18nLocale),
    getReferenceTypeNameFilter(i18nLocale),
    getReferenceSourceTypeNameFilter(i18nLocale),
    getReferenceTargetTypeNameFilter(i18nLocale),
    getSourceFilter(i18nLocale),
    getTargetFilter(i18nLocale),
  ],
});

/** @deprecated Use {@link getDefaultFiltersBySearchContext} with an `I18nLocale`. */
export const DEFAULT_FILTERS_BY_SEARCH_CONTEXT: Record<
  SearchContext,
  QueryBuilderFilterDefinition[]
> = getDefaultFiltersBySearchContext(DEFAULT_ARDOQ_I18NLOCALE);

export const getDynamicFilterSelectOptions = (
  i18nLocale: I18nLocale
): Record<
  'COMPONENT_CURRENT' | 'COMPONENT_PARENT_NONE',
  { label: string; value: string }
> => ({
  COMPONENT_CURRENT: {
    label: t(
      i18nLocale,
      'queryBuilder:dynamicFilterOption.selectedComponent',
      'Selected component'
    ),
    value: DynamicFilterValues.COMPONENT_CURRENT,
  },
  COMPONENT_PARENT_NONE: {
    label: t(
      i18nLocale,
      'queryBuilder:dynamicFilterOption.noParent',
      'No parent'
    ),
    value: DynamicFilterValues.COMPONENT_PARENT_NONE,
  },
});

/** @deprecated Use {@link getDynamicFilterSelectOptions} with an `I18nLocale`. */
export const DynamicFilterSelectOptions: Record<
  'COMPONENT_CURRENT' | 'COMPONENT_PARENT_NONE',
  { label: string; value: string }
> = getDynamicFilterSelectOptions(DEFAULT_ARDOQ_I18NLOCALE);
